const SITE_NAME = "Glastonbury Data";
const SITE_URL = "https://glastonburydata.com";
const DEFAULT_TITLE = `${SITE_NAME} | Glastonbury CT Budgets, Elections & Public Data`;
const DEFAULT_DESCRIPTION =
  "Explore Glastonbury, Connecticut town and school budgets, election results, police activity, property values, traffic counts, and campaign finance reports.";

const SEO_ROUTES = [
  ...[
    ["/properties/rent/summary", "Glastonbury Rent Summary", "Explore Glastonbury housing, rental affordability, population, and historical rental trends."],
    ["/properties/rent/census", "Glastonbury Census Rent Data", "Explore Census rental costs, rent burden, income, and historical ACS data for Glastonbury."],
    ["/properties/rent/hud", "Glastonbury HUD Rent Data", "Explore HUD Fair Market Rents and income limits for Glastonbury."],
    ["/properties", "Glastonbury Property Search", "Search Glastonbury assessment records by property type, address, owner, values, and building details."],
    ["/properties/types", "Glastonbury Property Types", "Browse Glastonbury property use descriptions, property counts, and median appraised values."],
    ["/properties/reports/values", "Glastonbury Property Values", "Compare assessed and appraised values by Glastonbury property type."],
    ["/properties/reports/sale-vs-appraised", "Glastonbury Sale vs Appraised Values", "Compare recorded sale prices with imported appraised values in dollars and percentages."],
    ["/properties/reports/recorded-sales", "Glastonbury Recorded Sales", "Explore Glastonbury recorded property sales by year."],
    ["/properties/reports/sale-counts", "Glastonbury Sale Counts", "Explore recorded property sale counts, median prices, and average prices by year."],
    ["/properties/map", "Glastonbury Property Map", "Find Glastonbury properties on a map and explore their assessment records."],
    ["/properties/taxes", "Find My Glastonbury Property Taxes", "Find imported property tax bills by address and estimate taxes with a chosen mill rate."],
  ].map(([path,title,description]) => ({path, canonicalPath:path, title:`${title} | Glastonbury Data`, description})),
  {path:"/properties/reports",canonicalPath:"/properties/reports/values",title:"Property Values | Glastonbury Data",description:"Explore Glastonbury property values."},
  {path:"/properties/research",canonicalPath:"/properties/research",title:"Property Appraisal Research | Glastonbury Data",description:"A dated research snapshot of sale-versus-appraisal patterns, price bands, MBLU geography, and data verification for 2017–2026.",noindex:true},
  {path:"/properties/:propertyId",title:"Glastonbury Property Record | Glastonbury Data",description:"View property ownership, assessments, appraisal history, building details, and imported tax bills.",noindex:true},
  {
    path: "/",
    canonicalPath: "/",
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  {
    path: "/elections", canonicalPath: "/elections",
    title: "Glastonbury CT Election Results & Voter Turnout | Glastonbury Data",
    description: "Explore Glastonbury, Connecticut federal, state, and local election results, voter turnout, registration trends, and Town Council voting simulations.",
  },
  { path: "/maps", canonicalPath: "/tax-density-map", title: "Tax Density Map | Glastonbury Data", description: "Explore assessed and appraised property values in Glastonbury, Connecticut." },
  { path: "/login", canonicalPath: "/login", title: "Sign In | Glastonbury Data", description: "Sign in to Glastonbury Data.", noindex: true },
  { path: "/register", canonicalPath: "/register", title: "Register | Glastonbury Data", description: "Create a Glastonbury Data account.", noindex: true },

  {
    path: "/budget",
    canonicalPath: "/budget",
    title: "Glastonbury CT Town Budget | Glastonbury Data",
    description:
      "Review Glastonbury town budget revenues, expenditures, projections, and program-level details with tables and charts.",
  },
  {
    path: "/town",
    canonicalPath: "/budget",
    title: "Glastonbury CT Town Budget | Glastonbury Data",
    description:
      "Review Glastonbury town budget revenues, expenditures, projections, and program-level details with tables and charts.",
  },
  {
    path: "/traffic-density",
    canonicalPath: "/traffic-density",
    title: "Traffic Density | Glastonbury Data",
    description: "Explore historical hourly traffic counts and road traffic maps in Glastonbury, Connecticut.",
  },
  {
    path: "/tax-density-map",
    canonicalPath: "/tax-density-map",
    title: "Tax Density Map | Glastonbury Data",
    description:
      "Review Glastonbury property value density by map block using MBLU-based assessment and appraisal aggregates.",
  },
  {
    path: "/reports",
    canonicalPath: "/reports",
    title: "Glastonbury CT School Budget | Glastonbury Data",
    description:
      "Explore Glastonbury Board of Education budget summaries, staffing, enrollment, PPE, and program-level spending data.",
  },
  {
    path: "/boe/maps/residences",
    canonicalPath: "/boe/maps/residences",
    title: "3D Residential Properties | Glastonbury Data",
    description: "Explore Glastonbury residential property counts on a 3D map with a 300-meter grid.",
  },
  {
    path: "/boe/maps",
    canonicalPath: "/boe/maps",
    title: "3D School Populations | Glastonbury Data",
    description: "Explore Glastonbury school enrollment by location and fiscal year on an interactive 3D map.",
  },
  {
    path: "/reports/maps",
    canonicalPath: "/boe/maps",
    title: "3D School Populations | Glastonbury Data",
    description: "Explore Glastonbury school enrollment by location and fiscal year on an interactive 3D map.",
  },
  {
    path: "/boe",
    canonicalPath: "/reports",
    title: "Glastonbury CT School Budget | Glastonbury Data",
    description:
      "Explore Glastonbury Board of Education budget summaries, staffing, enrollment, PPE, and program-level spending data.",
  },
  {
    path: "/budget-vs-spent",
    canonicalPath: "/budget-vs-spent",
    title: "Budget vs Spent | Glastonbury Data",
    description:
      "Compare approved budgets against actual spending across Glastonbury town and Board of Education data to find major variances and outliers.",
  },
  {
    path: "/crime",
    canonicalPath: "/crime",
    title: "Crime and Police Activity | Glastonbury Data",
    description:
      "Explore monthly Glastonbury police calls for service by published call type and compare activity across calendar years.",
  },
  {
    path: "/seec",
    canonicalPath: "/seec",
    title: "Campaign Finance Reports | Glastonbury Data",
    description:
      "Search local campaign finance receipts, expenditures, and summary filings with Glastonbury-focused SEEC reporting tools.",
  },
  {
    path: "/seec/top-donors",
    canonicalPath: "/seec/top-donors",
    title: "Top Donors | Glastonbury Data",
    description:
      "Browse top campaign donors, filter by town or organization, and review contribution trends in Glastonbury-area SEEC data.",
  },
  {
    path: "/seec/donor/:docId",
    canonicalPath: null,
    title: "Donor Profile | Glastonbury Data",
    description:
      "View campaign finance donor details, contribution history, locations, organizations, and yearly totals in Glastonbury Data.",
  },
  {
    path: "/250",
    canonicalPath: "/250",
    title: "America 250 Events | Glastonbury Data",
    description:
      "See upcoming America 250 events, organizers, and location details collected for Glastonbury community planning and reference.",
  },
];

function structuredData(entry, canonicalUrl) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "WebSite", "@id": `${SITE_URL}/#website`, url: `${SITE_URL}/`, name: SITE_NAME, description: DEFAULT_DESCRIPTION, inLanguage: "en-US" },
      { "@type": "WebPage", "@id": `${canonicalUrl}#webpage`, url: canonicalUrl, name: entry.title, description: entry.description, isPartOf: { "@id": `${SITE_URL}/#website` }, inLanguage: "en-US", about: { "@type": "Place", name: "Glastonbury, Connecticut" } },
    ],
  };
}
module.exports = { SITE_NAME, SITE_URL, DEFAULT_TITLE, DEFAULT_DESCRIPTION, SEO_ROUTES, structuredData };
